import { useState, useEffect, useCallback, useRef } from 'react';
import * as ApiService from '../services/apiService.js';
import { getAllTeamMembers } from '../services/azureTeamService.js';

// Aktuell verfuegbare, AbaNinja-gestuetzte Team-Mitglieder statt der rohen
// AbaNinja-Mitarbeiterliste (die auch laengst ausgeschiedene Personen enthaelt).
// employeeUuid ist gemaess ADR 0003 die kanonische ID und entspricht bei
// AbaNinja-gefuehrten Personen deren AbaNinja-UUID, daher bleibt die Form
// {uuid, firstName, lastName} fuer bestehende Verbraucher (Header, ProjectForm) kompatibel.
const today = () => new Date().toISOString().slice(0, 10);

const loadCurrentTeamAsEmployees = async () => {
  const team = await getAllTeamMembers();
  const now = today();
  return team
    .filter(
      (tm) =>
        tm.employeeUuid &&
        tm.imTeam !== false &&
        tm.kategorie !== "Freelancer" &&
        tm.kategorie !== "El Gato Muerto" &&
        (!tm.start || tm.start <= now) &&
        (!tm.ende || tm.ende >= now)
    )
    .map((tm) => ({
      uuid: tm.employeeUuid,
      firstName: tm.name?.split(" ")[0] || "",
      lastName: tm.name?.split(" ").slice(1).join(" ") || "",
      // App.jsx matcht die eingeloggte Person per E-Mail gegen dieses Feld
      // (historisch aus der AbaNinja-Form {user: {email}} uebernommen).
      user: { email: tm.email || "" },
    }))
    .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
};

export const useApiData = (apiConnected) => {
  // Tier 1: Always loaded (needed by Header on every page)
  const [employees, setEmployees] = useState([]);

  // Tier 2: Lazy-loaded on demand, cached once fetched
  const [contracts, setContracts] = useState([]);
  const [allContracts, setAllContracts] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track what has been loaded to avoid re-fetching
  const loadedRef = useRef({ employees: false, detailData: false });

  // Tier 1: Load employees eagerly.
  // Quelle ist die interne Team-Tabelle (aktuell aktive Mitglieder), nicht mehr
  // die rohe AbaNinja-Mitarbeiterliste — sonst waeren auch ausgeschiedene
  // Personen im Mitarbeiter-Wechsel (Header) und bei "Projektleitung" waehlbar.
  const loadEmployees = useCallback(async () => {
    if (!apiConnected || loadedRef.current.employees) return;
    try {
      setLoading(true);
      setError(null);
      const employeesData = await loadCurrentTeamAsEmployees();
      setEmployees(employeesData);
      loadedRef.current.employees = true;
    } catch (err) {
      setError('Fehler beim Laden der Mitarbeiter: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiConnected]);

  // Tier 2: Load detail data on demand (contracts, activityTypes, products, invoices)
  const loadDetailData = useCallback(async () => {
    if (!apiConnected || loadedRef.current.detailData) return;
    try {
      setLoading(true);
      setError(null);

      const [
        activityTypesData,
        contractsData,
        invoicesData,
        productsData,
      ] = await Promise.all([
        ApiService.getActivityTypes(),
        ApiService.getContracts(),
        ApiService.getInvoices(),
        ApiService.getProducts(),
      ]);

      setActivityTypes(activityTypesData);
      setContracts(contractsData.filter(c => !c.archivedAt));
      setProducts(productsData);
      setInvoices(invoicesData);

      const allContractsData = await ApiService.getAllContractsForProjects();
      setAllContracts(allContractsData);

      loadedRef.current.detailData = true;
    } catch (err) {
      setError('Fehler beim Laden der Daten: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiConnected]);

  // Auto-load employees when API connects
  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  // Reset cache when API disconnects
  useEffect(() => {
    if (!apiConnected) {
      loadedRef.current = { employees: false, detailData: false };
      setEmployees([]);
      setContracts([]);
      setAllContracts([]);
      setActivityTypes([]);
      setProducts([]);
      setInvoices([]);
    }
  }, [apiConnected]);

  return {
    employees,
    contracts,
    allContracts,
    activityTypes,
    products,
    invoices,
    loading,
    error,
    loadDetailData,
    reloadData: async () => {
      loadedRef.current = { employees: false, detailData: false };
      await loadEmployees();
      await loadDetailData();
    },
  };
};
